/**
 * CreateAccountModal — модалка создания аккаунта (ADMIN_SPEC §5, дизайн
 * «Create Account»). Выбор типа (клиника/соло), контролируемые поля под
 * тип, живой дедуп телефона (phone-check), город из справочника, степпер
 * мест со ступенчатой ценой. Создаётся в статусе Триал на 30 дней.
 *
 * Успех: панель-подсказка «вход по OTP» (паролей в продукте нет — владелец
 * заходит по коду на свой номер), список клиентов инвалидируется.
 *
 * Можно открыть «по заявке» — тогда форма предзаполнена тем, что человек
 * сам о себе сказал, а аккаунт связывается с очередью через
 * `applicationId`. Предзаполнение читается из начального состояния, а не
 * синхронизируется эффектом: модалку в этом режиме монтируют с `key` по
 * заявке, так что состояние заводится один раз и менеджер не теряет
 * правки от случайного перерендера.
 *
 * Чего намеренно НЕ подставляем — число мест. В заявке лежит диапазон
 * («6–10 врачей»), а `seats` — цифра в счёте. Превратить одно в другое
 * можно только догадкой, а догадка здесь стоит денег: это выясняется на
 * звонке. Поле остаётся на своём умолчании (DEFAULT_SEATS), и об этом
 * сказано на кнопке в карточке заявки — иначе менеджер решит, что места
 * подставлены из заявки, и выпустит счёт не на то число врачей.
 */

import {type ReactNode, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {Building2, Check, CircleAlert, Minus, Plus, User, X} from 'lucide-react';

import type {ApplicationItem} from '@services/applications';
import {
  clinicQuoteFor,
  type ClinicType,
  createAccount,
  LANGUAGE_OPTIONS,
  SOLO_PRICE,
} from '@services/clients';
import {tariffsQueryOptions} from '@services/tariffs';
import {keys} from '@queries/keys';
import {useCityOptions} from '@hooks/useFilterRefs';
import {usePhoneOwner} from '@hooks/usePhoneOwner';
import {useToast} from '@hooks/useToast';
import {Field, TextInput} from '@components/FormKit';
import {Toast} from '@components/Toast';
import {Button} from '@components/ui/Button';
import {Dialog, DialogContent} from '@components/ui/Dialog';
import {cn} from '@lib/utils';
import {formatNumber} from '@utils/format';
import {formatKgPhone, phoneDigits} from '@utils/phone';

import {createAccountFailure, DEFAULT_SEATS} from './CreateAccountModal.utils';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Заявка из очереди — предзаполняет форму и связывает аккаунт с ней */
  application?: ApplicationItem | null;
  /**
   * Клиент заведён и менеджер нажал «Готово».
   *
   * Нужно тем, кто монтирует модалку условно: собственный тост умирает
   * вместе с размонтированием, и «Аккаунт создан» никто не увидит.
   * Передали коллбэк — показывает вызывающий, не передали — модалка сама.
   */
  onCreated?: (clinicId: string) => void;
};

export const CreateAccountModal = ({open, onOpenChange, application = null, onCreated}: Props) => {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ClinicType>('clinic');
  const [seats, setSeats] = useState(DEFAULT_SEATS);
  /** Созданная клиника — её id уносим наверх по «Готово» */
  const [clinicId, setClinicId] = useState('');
  const [phone, setPhone] = useState(() => phoneDigits(application?.phone ?? ''));
  const [clinicName, setClinicName] = useState(application?.companyName ?? '');
  const [city, setCity] = useState(application?.cityName ?? '');
  const [language, setLanguage] = useState<string>(LANGUAGE_OPTIONS[0]);
  const [address, setAddress] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');
  /* Имя человека кладём в оба поля: менеджер переключит тип клиента, и
     введённое не должно пропасть вместе с переключением. */
  const [adminFullName, setAdminFullName] = useState(application?.fullName ?? '');
  const [fullName, setFullName] = useState(application?.fullName ?? '');
  /** Успех: панель «вход по OTP» вместо формы */
  const [created, setCreated] = useState(false);
  const {toast, notify} = useToast();

  const cityOptions = useCityOptions();
  const isClinic = type === 'clinic';
  const {owner, checked} = usePhoneOwner(phone);
  const taken = owner !== null;

  // Цены — из активной версии тарифов (live); до загрузки — константы мока
  const {data: tariffs} = useQuery(tariffsQueryOptions());
  const price = isClinic
    ? clinicQuoteFor(seats, tariffs?.clinic.packages, tariffs?.clinic.overPrice)
    : {sum: tariffs?.solo.price ?? SOLO_PRICE, tier: 'фикс.', range: ''};

  const mutation = useMutation({
    mutationFn: createAccount,
    onSuccess: (result) => {
      setClinicId(result.id);
      void queryClient.invalidateQueries({queryKey: keys.clients.all});
      if (application !== null) {
        /* Карточку заявки правим вручную, а не только инвалидируем.
           Инвалидация неактивный запрос лишь помечает устаревшим, и
           дровер, открытый сразу после создания, успевает отрисовать
           заявку без `convertedClinicId` — то есть снова предложить
           завести клиента, который только что заведён. Идентификатор
           клиники — факт из ответа бэка, а не наша догадка. */
        queryClient.setQueryData(
          keys.applications.detail(application.id),
          (prev: ApplicationItem | null | undefined) =>
            prev == null ? prev : {...prev, convertedClinicId: result.id},
        );
        void queryClient.invalidateQueries({queryKey: keys.applications.all});
      }
      setCreated(true);
    },
  });

  const phoneOk = phoneDigits(phone).length === 9 && !taken;
  const valid = isClinic
    ? phoneOk && clinicName.trim().length >= 2 && adminFullName.trim().length >= 2 && city !== ''
    : phoneOk && fullName.trim().length >= 2;

  const handleSubmit = () => {
    if (!valid) return;
    mutation.mutate({
      type,
      adminPhone: phone,
      seats: isClinic ? seats : undefined,
      clinicName: clinicName || undefined,
      city: city || undefined,
      language,
      address: address || undefined,
      businessPhone: businessPhone || undefined,
      adminFullName: adminFullName || undefined,
      fullName: fullName || undefined,
      applicationId: application?.id,
    });
  };

  const handleDone = () => {
    setCreated(false);
    onOpenChange(false);
    if (onCreated === undefined) notify('Аккаунт создан · триал 30 дней', 2400);
    else onCreated(clinicId);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          showClose={false}
          className="flex max-h-[92vh] w-[540px] max-w-[calc(100vw-2rem)] flex-col gap-0 overflow-hidden rounded-[20px] p-0"
        >
          <div className="flex items-center justify-between border-b border-skr-hairline px-6 pb-4 pt-5">
            <div>
              <h3 className="text-lg font-bold -tracking-[.01em]">
                {created ? 'Аккаунт создан' : 'Новый аккаунт'}
              </h3>
              <p className="mt-0.5 text-[12.5px] text-muted-foreground">
                {created
                  ? 'Владелец может заходить в кабинет'
                  : application !== null
                    ? 'По заявке из очереди · триал 30 дней'
                    : 'Создаётся в статусе «Триал» на 30 дней'}
              </p>
            </div>
            <button
              onClick={() => onOpenChange(false)}
              aria-label="Закрыть"
              className="grid size-8 place-items-center rounded-[9px] bg-secondary text-muted-foreground"
            >
              <X className="size-[17px]" />
            </button>
          </div>

          {created ? (
            <div className="flex flex-col gap-4 px-6 pb-5 pt-5">
              <div className="flex gap-3 rounded-[13px] border border-skr-info-border bg-skr-info p-4">
                <span className="grid size-10 shrink-0 place-items-center rounded-[11px] bg-skr-info-chip text-primary">
                  <Check className="size-5" strokeWidth={2.2} />
                </span>
                <div className="text-[12.5px] leading-[1.5] text-skr-info-ink">
                  <div className="font-semibold">Пароль не нужен — вход по OTP</div>
                  Владелец заходит в кабинет по своему номеру телефона: запрашивает код
                  подтверждения и вводит его.
                </div>
              </div>
              <Button className="h-[46px] rounded-[11px]" onClick={handleDone}>
                Готово
              </Button>
            </div>
          ) : (
            <>
              <div className="flex-1 overflow-y-auto px-6 pb-2 pt-5">
                <Label>Тип клиента</Label>
                <div className="mb-[22px] mt-2.5 flex gap-[11px]">
                  <TypeCard
                    active={isClinic}
                    icon={<Building2 className="size-5" strokeWidth={1.7} />}
                    title="Клиника"
                    subtitle="Админ + врачи, оплата за места"
                    onClick={() => setType('clinic')}
                  />
                  <TypeCard
                    active={!isClinic}
                    icon={<User className="size-5" strokeWidth={1.7} />}
                    title="Соло-врач"
                    subtitle="Один врач, фикс. цена"
                    onClick={() => setType('solo')}
                  />
                </div>

                {isClinic ? (
                  <div className="flex flex-col gap-3.5">
                    <Field label="Название клиники">
                      <TextInput
                        placeholder="Например, Дентал Плюс"
                        value={clinicName}
                        onChange={(e) => setClinicName(e.target.value)}
                      />
                    </Field>
                    <div className="flex gap-[11px]">
                      <Field label="Город" className="flex-1">
                        <Select value={city} onChange={setCity} options={cityOptions} />
                      </Field>
                      <Field label="Язык кабинета" className="flex-1">
                        <Select
                          value={language}
                          onChange={setLanguage}
                          options={[...LANGUAGE_OPTIONS]}
                        />
                      </Field>
                    </div>
                    <Field label="Адрес">
                      <TextInput
                        placeholder="Улица, дом, офис"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                    </Field>
                    <Field label="Телефон клиники">
                      <TextInput
                        placeholder="700 000 000"
                        mono
                        value={formatKgPhone(phoneDigits(businessPhone))}
                        onChange={(e) => setBusinessPhone(phoneDigits(e.target.value))}
                      />
                    </Field>

                    <Divider>Администратор</Divider>
                    <Field label="ФИО администратора">
                      <TextInput
                        placeholder="Кто управляет клиникой"
                        value={adminFullName}
                        onChange={(e) => setAdminFullName(e.target.value)}
                      />
                    </Field>
                    <PhoneField
                      label="Телефон администратора · логин"
                      value={phone}
                      onChange={setPhone}
                      showHint={checked}
                      owner={owner}
                    />
                    <Field label="Сколько врачей будет работать">
                      <div className="flex items-center gap-3.5">
                        <Stepper
                          value={seats}
                          onDec={() => setSeats((s) => Math.max(2, s - 1))}
                          onInc={() => setSeats((s) => Math.min(15, s + 1))}
                        />
                        <div className="text-[12.5px] leading-[1.4] text-muted-foreground">
                          врачей на платных местах
                          <br />
                          админ — бесплатно
                        </div>
                      </div>
                    </Field>
                  </div>
                ) : (
                  <div className="flex flex-col gap-3.5">
                    <Field label="ФИО врача">
                      <TextInput
                        placeholder="Например, Асанов Бакыт"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                      />
                    </Field>
                    <PhoneField
                      label="Телефон · логин"
                      value={phone}
                      onChange={setPhone}
                      showHint={checked}
                      owner={owner}
                    />
                    <div className="flex gap-[11px]">
                      <Field label="Город" className="flex-1">
                        <Select value={city} onChange={setCity} options={cityOptions} />
                      </Field>
                      <Field label="Язык кабинета" className="flex-1">
                        <Select
                          value={language}
                          onChange={setLanguage}
                          options={[...LANGUAGE_OPTIONS]}
                        />
                      </Field>
                    </div>
                    <Field label="Адрес приёма">
                      <TextInput
                        placeholder="Улица, дом"
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                      />
                    </Field>
                  </div>
                )}

                {mutation.isError && (
                  <div className="mt-3 rounded-[10px] bg-destructive/10 px-3.5 py-2.5 text-[12.5px] font-medium text-destructive">
                    {createAccountFailure(mutation.error)}
                  </div>
                )}
              </div>

              {/* price bar */}
              <div className="mx-6 mt-2 flex items-center justify-between rounded-xl border border-skr-info-border bg-skr-info px-4 py-[13px]">
                <div>
                  <div className="text-[12.5px] font-semibold text-skr-info-ink2">
                    {isClinic ? `Тариф Клиника · ${price.tier}` : 'Тариф Соло · фикс.'}
                  </div>
                  <div className="mt-px text-[11.5px] text-skr-info-faint">
                    {isClinic ? `${price.range} · админ бесплатно` : 'фиксированная цена'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-semibold text-skr-info-ink">
                    {formatNumber(price.sum)}
                  </div>
                  <div className="text-[11px] text-skr-info-faint">сом / мес</div>
                </div>
              </div>

              <div className="flex gap-2.5 px-6 pb-5 pt-4">
                <Button
                  variant="outline"
                  className="h-[46px] rounded-[11px] px-[18px]"
                  onClick={() => onOpenChange(false)}
                >
                  Отмена
                </Button>
                <Button
                  className="h-[46px] flex-1 rounded-[11px]"
                  disabled={!valid || mutation.isPending}
                  onClick={handleSubmit}
                >
                  {isClinic ? 'Создать клинику' : 'Создать соло-врача'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Toast
        message={toast}
        icon={<Check className="size-4 text-skr-toast-dot" strokeWidth={2.4} />}
      />
    </>
  );
};

/* ── локальные под-элементы модалки ────────────────────────────── */

const Label = ({children}: {children: ReactNode}) => (
  <div className="text-[12.5px] font-semibold text-skr-ink2">{children}</div>
);

/** Нативный селект в стиле TextInput (город/язык) */
const Select = ({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: string[];
}) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    className={cn(
      'h-11 w-full appearance-none rounded-[10px] border border-input bg-card px-[13px] text-sm outline-none transition',
      'focus:border-primary focus:ring-[3px] focus:ring-primary/12',
      value === '' && 'text-muted-foreground',
    )}
  >
    <option value="" disabled>
      Выберите
    </option>
    {options.map((o) => (
      <option key={o} value={o}>
        {o}
      </option>
    ))}
  </select>
);

type TypeCardProps = {
  active: boolean;
  icon: ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
};
const TypeCard = ({active, icon, title, subtitle, onClick}: TypeCardProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'flex-1 rounded-[13px] border-[1.5px] p-[14px_15px] text-left transition-colors',
      active ? 'border-primary bg-skr-info' : 'border-skr-line bg-card',
    )}
  >
    <div className="flex items-center justify-between">
      <span
        className={cn(
          'grid size-9 place-items-center rounded-[10px] [&_svg]:size-5',
          active ? 'bg-accent text-primary' : 'bg-secondary text-muted-foreground',
        )}
      >
        {icon}
      </span>
      <span
        className={cn(
          'grid size-5 place-items-center rounded-full border-2',
          active ? 'border-primary' : 'border-input',
        )}
      >
        {active && <span className="size-[9px] rounded-full bg-primary" />}
      </span>
    </div>
    <div className="mt-2.5 text-[14.5px] font-semibold">{title}</div>
    <div className="mt-0.5 text-xs leading-[1.4] text-muted-foreground">{subtitle}</div>
  </button>
);

const Divider = ({children}: {children: ReactNode}) => (
  <div className="mt-1 flex items-center gap-2.5">
    <div className="h-px flex-1 bg-skr-hairline" />
    <span className="text-[11.5px] font-semibold uppercase tracking-[.05em] text-skr-label">
      {children}
    </span>
    <div className="h-px flex-1 bg-skr-hairline" />
  </div>
);

const Stepper = ({value, onDec, onInc}: {value: number; onDec: () => void; onInc: () => void}) => (
  <div className="flex items-center overflow-hidden rounded-[10px] border border-input">
    <button
      onClick={onDec}
      className="grid size-11 place-items-center border-r border-input bg-skr-head text-skr-ink hover:bg-muted"
    >
      <Minus className="size-[18px]" strokeWidth={2.2} />
    </button>
    <div className="grid h-11 w-14 place-items-center font-mono text-[17px] font-semibold">
      {value}
    </div>
    <button
      onClick={onInc}
      className="grid size-11 place-items-center border-l border-input bg-skr-head text-skr-ink hover:bg-muted"
    >
      <Plus className="size-[18px]" strokeWidth={2.2} />
    </button>
  </div>
);

/** Телефон с маской 700 123 456 и живым вердиктом «свободен/занят» */
const PhoneField = ({
  label,
  value,
  onChange,
  showHint,
  owner,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  showHint: boolean;
  owner: string | null;
}) => {
  const taken = owner !== null;
  return (
    <Field label={label}>
      <div
        className={cn(
          'flex h-11 items-center gap-2 rounded-[10px] border px-[13px] transition',
          !showHint && 'border-input',
          showHint && taken && 'border-destructive/40 ring-[3px] ring-skr-danger/10',
          showHint && !taken && 'border-success/40 ring-[3px] ring-skr-paid/10',
        )}
      >
        <span className="shrink-0 font-mono text-sm text-muted-foreground">+996</span>
        <input
          value={formatKgPhone(phoneDigits(value))}
          onChange={(e) => onChange(phoneDigits(e.target.value))}
          type="tel"
          inputMode="tel"
          placeholder="700 000 000"
          className="flex-1 border-none bg-transparent font-mono text-sm outline-none placeholder:text-muted-foreground"
        />
        {showHint &&
          (taken ? (
            <CircleAlert className="size-[17px] text-skr-danger" strokeWidth={2} />
          ) : (
            <Check className="size-[17px] text-skr-paid" strokeWidth={2.2} />
          ))}
      </div>
      {showHint && (
        <div className={cn('text-xs', taken ? 'text-skr-danger' : 'text-skr-paid')}>
          {taken ? `Этот номер уже привязан к клиенту «${owner}»` : 'Номер свободен'}
        </div>
      )}
    </Field>
  );
};
